import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE_URL, SITE_NAME, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE } from './siteConfig';
import { organizationSchema, websiteSchema } from './schemas';

/**
 * SEO — single source of per-page meta. Pass title + description tuned to a
 * keyword cluster for that page. Anything not passed falls back to site-wide
 * defaults.
 *
 * Props:
 *   title        — page title (will be appended with " | Shipmaster Fulfillment")
 *   description  — meta description (~150-160 chars, keyword-rich)
 *   keywords     — comma-separated keywords (light SEO signal but cheap to add)
 *   image        — absolute URL of social card (1200x630 recommended)
 *   noindex      — bool, set true for thin / utility pages
 *   schemas      — array of additional JSON-LD objects (e.g. FAQPage, Article)
 *   canonical    — override path (defaults to current pathname)
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords,
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  schemas = [],
  canonical,
}) {
  const { pathname } = useLocation();
  const path = canonical || pathname;
  const url = `${SITE_URL}${path === '/' ? '' : path}`;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Modern 3PL for Shopify & DTC Brands`;

  // Always include Organization + WebSite schema on every page.
  const allSchemas = [organizationSchema(), websiteSchema(), ...schemas];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {allSchemas.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
